import { Clickable } from '@carvana/merch-components';
import { TagWrapper } from '@/home/HeroCardsReturnUser/LinkCards/VDPLinks/StatusTag';
import TrackWrapper from '~/components/common/TrackWrapper';
import SortOption from '~/constants/common/SortOption';
import Sections from '~/constants/home/sections';
import { useGetRecentSearches } from '~/context/home/RecentSearchesContext';
import type { RecentSearch } from '~/contracts/home';
import { useGetConsumerHref } from '~/hooks/common';
import useCarouselSettings from '~/hooks/home/useCarouselSettings';
import useToggleSavedSearches, {
  ToggleSavedSearchSource
} from '~/hooks/home/useToggleSavedSearches';
import HeartWithBorder from '~/icons/HeartWithBorder';
import { nativeSrpPayload } from '~/native/common/utils';
import getRelativeDate from '~/utilities/date/getRelativeDate';
import decodeBase64 from '~/utilities/decodeBase64';
import encodeBase64 from '~/utilities/encodeBase64';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import {
  TileTopSectionContent,
  TileTopSectionWrapper,
  TileWrapper
} from './RecentSearches.styles';
import Carousel from '../Carousel';

type RecentSearchTileProps = {
  tileInfo: RecentSearch;
};

export const addNewestInventorySortToCvnaId = (cvnaId: string): string => {
  const { filters } = JSON.parse(decodeBase64(cvnaId));

  return encodeBase64(
    JSON.stringify({
      filters,
      sortBy: SortOption.NewestInventory
    })
  );
};

function RecentSearchTile({ tileInfo }: RecentSearchTileProps) {
  const {
    cvnaId,
    savedSearch: { savedSearchId = 0, name = '' } = {},
    display: { name: displayName },
    totalMatchingVehicles = -1,
    totalNewVehicles,
    searchExecutedDateTime
  } = tileInfo;
  const getHref = useGetConsumerHref();
  const alertName = name || displayName;
  const toggleSavedSearch = useToggleSavedSearches({
    search: tileInfo,
    source: ToggleSavedSearchSource.HomeFeed
  });

  return (
    <a
      href={getHref(
        `/cars/filters?cvnaid=${addNewestInventorySortToCvnaId(cvnaId)}`
      )}
      data-analytics-id="Home feed card"
      data-analytics-attributes={JSON.stringify({
        description: 'Recent search tile',
        content: tileInfo.display
      })}
      data-native-payload={nativeSrpPayload}
      data-qa="recent-search-tile"
    >
      <TileWrapper>
        <TileTopSectionWrapper>
          <TileTopSectionContent>
            <div className="tl-body-xs" title={searchExecutedDateTime}>
              {getRelativeDate(searchExecutedDateTime)}
            </div>
            <div
              className="line-clamp-2 text-blue-6 tl-header-s"
              data-qa="recent-searches-alert-name"
            >
              {alertName}
            </div>
          </TileTopSectionContent>
          <Clickable
            onClick={toggleSavedSearch}
            data-analytics-id={null} // event handled in the toggle hook
            data-qa="saved search"
            className="pointer-events-auto"
          >
            <HeartWithBorder
              darkBlue
              isFavorited={Boolean(savedSearchId)}
              className="h-20 w-20"
            />
          </Clickable>
        </TileTopSectionWrapper>
        <div className="flex items-center gap-8">
          {totalMatchingVehicles >= 0 && (
            <div className="tl-body-s" data-qa="matched-results">
              {totalMatchingVehicles.toLocaleString()} result
              {totalMatchingVehicles > 1 ? 's' : ''}
            </div>
          )}
          {totalNewVehicles ? (
            <TagWrapper className="bg-marketplace-purple text-white">
              {totalNewVehicles.toLocaleString()} New
            </TagWrapper>
          ) : null}
        </div>
      </TileWrapper>
    </a>
  );
}

function RecentSearches() {
  const recentSearches = useGetRecentSearches();
  const { values, carouselRef } = useCarouselSettings({
    heading: 'Recent Searches',
    maxLength: recentSearches?.length
  });

  const viewEvent = {
    eventName: 'Viewed - Home feed section',
    customAttributes: {
      description: 'Recent Searches',
      isCarousel: true,
      content: {
        searchTerms: recentSearches
      }
    }
  };

  return (
    <>
      {recentSearches.length > 0 ? (
        <TrackWrapper event={viewEvent}>
          <Carousel
            {...values}
            ref={carouselRef}
            dataQa="recent-searches"
            isNonVehicleCarousel
          >
            {recentSearches.map((recentSearch: RecentSearch) => {
              return (
                <RecentSearchTile
                  key={recentSearch.display.name}
                  tileInfo={recentSearch}
                />
              );
            })}
          </Carousel>
        </TrackWrapper>
      ) : null}
    </>
  );
}

export default withErrorBoundary(
  RecentSearches,
  undefined,
  undefined,
  Sections.RecentSearches
);
